import { clsx } from 'clsx';
import { Sprinkles, sprinkles } from '@design-system/sprinkles.css';
import { col, grid, row, responsiveRow } from './grid.css';
import { forwardRef, PropsWithChildren } from 'react';
import { space } from '../../tokens';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '../typography/typography';
import { FormTooltip, TooltipProps } from '../tooltip/tooltip';

export type GridProps = React.PropsWithChildren<
  Pick<
    Sprinkles,
    'p' | 'bg' | 'flexDirection' | 'justifyContent' | 'alignItems'
  > & {
    className?: string;
  }
>;

export const Grid = (p: GridProps) => (
  <div
    className={clsx(
      grid,
      sprinkles({
        p: p.p,
        flexDirection: p.flexDirection,
      }),
      p.className,
    )}
  >
    {p.children}
  </div>
);

export type RowProps = {
  gap?: GapSize;
  className?: string;
  justifyEnd?: boolean;
  justifyCenter?: boolean;
  alignCenter?: boolean;
  spreadEvenly?: boolean;
  responsive?: boolean;
};
export const Row = forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<RowProps & React.HTMLAttributes<HTMLDivElement>>
>(({ gap, className, justifyEnd, alignCenter, justifyCenter, ...p }, ref) => (
  <div
    ref={ref}
    className={clsx(
      p.responsive ? responsiveRow : row,
      sprinkles({
        gap: gap || 'm',
        ...(justifyCenter ? { justifyContent: 'center' } : {}),
        ...(justifyEnd ? { justifyContent: 'end' } : {}),
        ...(alignCenter ? { alignItems: 'center' } : {}),
        ...(p.spreadEvenly ? { justifyContent: 'space-between' } : {}),
      }),
      className,
    )}
    {...p}
  >
    {p.children}
  </div>
));

type GapSize = keyof typeof space;
type BoxProps = { height?: GapSize; width?: GapSize };
export const Box = (p: PropsWithChildren<BoxProps>) => (
  <div
    style={{
      ...(p.height ? { height: space[p.height] } : {}),
      ...(p.width ? { width: space[p.width] } : {}),
    }}
  >
    {p.children}
  </div>
);

export const Gap = (p: BoxProps) => <Box {...p} />;

export const Col = ({
  gap,
  className,
  ...p
}: React.PropsWithChildren<
  {
    gap?: GapSize;
    className?: string;
  } & React.HTMLAttributes<HTMLDivElement>
>) => (
  <div
    className={clsx(
      col,
      sprinkles({ width: '100%' }),
      className,
      sprinkles({ gap }),
    )}
    {...p}
  >
    {p.children}
  </div>
);

export const FormLabel = (
  p: PropsWithChildren<{
    tooltip?: Pick<TooltipProps, 'title'>['title'];
    hideTooltip?: boolean;
    icon?: IconProp;
    required?: boolean;
  }>,
) => (
  <Row
    gap="m"
    className={sprinkles({ alignItems: 'center', marginBottom: 'xs' })}
  >
    {p.icon ? <FontAwesomeIcon icon={p.icon} width={16} height={16} /> : null}
    <Typography variant="body" size="medium">
      {p.children}
      {p.required ? (
        <span className={sprinkles({ color: 'ERROR-100' })}>&nbsp;*</span>
      ) : null}
    </Typography>
    {p.hideTooltip || !p.tooltip ? null : (
      <FormTooltip title={p.tooltip} custom={typeof p.tooltip !== 'string'} />
    )}
  </Row>
);
